import { Alert, Badge, Box, Button, Divider, Flex, Group, Text } from '@mantine/core';
import { IconBrain, IconBuilding } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { MemoryProposalItemView, MemoryProposedAction } from '../chat/types';
import { ProposalDecidedLine } from './ProposalDecidedLine';
import { ProposalDiffBlock } from './ProposalDiffBlock';

type Props = {
  proposal: MemoryProposedAction;
  onDecide: (approve: boolean) => void;
  disabled?: boolean;
};

function itemHeading(item: MemoryProposalItemView): string {
  const action = item.isClear ? 'Clear' : 'Update';
  switch (item.scope) {
    case 'ADVISOR':
      return `${action} advisor memories`;
    case 'CLIENT':
      return item.clientName
        ? `${action} client memories — ${item.clientName}`
        : `${action} client memories`;
    case 'COMPANY':
      return `${action} company memories`;
  }
}

function ItemBlock({ item }: { item: MemoryProposalItemView }) {
  return (
    <Flex direction="column" gap="xxs">
      <Group gap="xs" align="center" wrap="wrap">
        <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9">
          {itemHeading(item)}
        </Text>
        {item.scope === 'COMPANY' && (
          <Badge
            size="sm"
            variant="light"
            color="warning"
            leftSection={<IconBuilding size={12} aria-hidden />}
          >
            Company-wide
          </Badge>
        )}
        <Text size="textXs" c="gray.6">
          +{item.addedCount} −{item.removedCount}
        </Text>
      </Group>
      {item.scope === 'COMPANY' && (
        <Alert color="warning" variant="light" p="xs">
          <Text size="textXs" c="gray.8">
            Company memories apply to everyone in the firm.
          </Text>
        </Alert>
      )}
      <ProposalDiffBlock lines={item.diffLines} truncated={item.diffTruncated} />
    </Flex>
  );
}

export function MemoryProposalWidget({ proposal, onDecide, disabled }: Props) {
  const isPending = proposal.status === 'PENDING' || !proposal.status;

  return (
    <Box bg="orange.1" p="md" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
      <Flex direction="column" gap="sm">
        <Group gap="xs" align="center" wrap="nowrap">
          <IconBrain size={16} stroke={1.5} color="var(--mantine-color-orange-7)" aria-hidden />
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9">
            Siesta wants to update memories
          </Text>
        </Group>
        {/* Everything below the reason is computed server-side from the stored proposal,
            never from Siesta's own description of what it did. */}
        {proposal.reason && (
          <Text size="textSm" c="gray.8">
            {proposal.reason}
          </Text>
        )}
        <Flex direction="column" gap="sm">
          {proposal.items.map((item, index) => (
            <Flex key={`${item.scope}-${index}`} direction="column" gap="sm">
              {index > 0 && <Divider color="orange.2" />}
              <ItemBlock item={item} />
            </Flex>
          ))}
        </Flex>
        {isPending ? (
          <Group gap="sm">
            <Button
              variant="filled"
              color="orange"
              size="sm"
              onClick={() => onDecide(true)}
              disabled={disabled}
            >
              Approve
            </Button>
            <Button variant="default" size="sm" onClick={() => onDecide(false)} disabled={disabled}>
              Deny
            </Button>
          </Group>
        ) : (
          <ProposalDecidedLine status={proposal.status} failureReason={proposal.failureReason} />
        )}
      </Flex>
    </Box>
  );
}
